import { digitalSaleState, saleFormats } from '@/lib/commerce/preorder-policy';
import { ROUTES } from '@/lib/routes';
import type { HomepageSettingsDoc, PlatformCollectionDoc, TitleDoc } from '@/types/platform';

export type ShopSortKey = 'bestselling' | 'newest' | 'price-asc' | 'price-desc' | 'title';

export type ShopCategoryId =
  | 'all'
  | 'boundaries'
  | 'healing'
  | 'manipulation'
  | 'self-trust'
  | 'recovery'
  | 'journals'
  | 'bundles';

export type ShopCategory = {
  id: ShopCategoryId;
  label: string;
  terms: string[];
};

export type ShopProduct = {
  id: string;
  cartId: number;
  itemType: 'title' | 'bundle';
  titleId?: string;
  collectionId?: string;
  slug: string;
  href: string;
  title: string;
  subtitle: string;
  description: string;
  category: string;
  tags: string[];
  formats: NonNullable<TitleDoc['formats']>;
  availableDigitalFormats?: Array<'epub' | 'pdf'>;
  price: number;
  compareAtPrice?: number;
  currency: 'EUR' | 'USD';
  coverUrl?: string;
  coverAlt: string;
  digitalSaleState?: TitleDoc['digitalSaleState'];
  canPurchase: boolean;
  featured: boolean;
  includedTitleIds?: string[];
  includedProducts?: ShopProduct[];
  discountLabel?: string;
  salesCount: number;
  updatedAt?: unknown;
  publishedAt?: unknown;
};

export type ShopBenefitItem = {
  title: string;
  description: string;
  icon: string;
};

export type ShopViewModel = {
  hero: {
    eyebrow: string;
    title: string;
    description: string;
    ctaLabel: string;
    ctaHref: string;
    imageUrl?: string;
    imageAlt: string;
  };
  categories: ShopCategory[];
  products: ShopProduct[];
  featuredCollection: {
    eyebrow: string;
    title: string;
    description: string;
    ctaLabel: string;
    imageUrl?: string;
    imageAlt: string;
    products: ShopProduct[];
  };
  bundle: {
    eyebrow: string;
    title: string;
    description: string;
    price: number;
    compareAtPrice?: number;
    currency: 'EUR' | 'USD';
    imageUrl?: string;
    imageAlt: string;
    products: ShopProduct[];
    product?: ShopProduct;
  } | null;
  benefits: ShopBenefitItem[];
  newsletter: {
    title: string;
    description: string;
    imageUrl?: string;
    imageAlt: string;
  };
};

export const SHOP_REFERENCE_CATEGORIES: ShopCategory[] = [
  { id: 'all', label: 'All Books', terms: [] },
  { id: 'boundaries', label: 'Boundaries', terms: ['boundaries', 'boundary', 'approval', 'guilt'] },
  { id: 'healing', label: 'Healing', terms: ['healing', 'heal', 'homecoming', 'coming home'] },
  { id: 'manipulation', label: 'Manipulation', terms: ['manipulation', 'gaslighting', 'control', 'delusion'] },
  { id: 'self-trust', label: 'Self-Trust', terms: ['self-trust', 'self trust', 'identity', 'within'] },
  { id: 'recovery', label: 'Recovery', terms: ['recovery', 'trauma bond', 'aftercare', 'recover'] },
  { id: 'journals', label: 'Journals', terms: ['journal', 'journals', 'workbook'] },
  { id: 'bundles', label: 'Bundles', terms: ['bundle', 'collection', 'set'] },
];

export const SHOP_SORT_OPTIONS: Array<{ value: ShopSortKey; label: string }> = [
  { value: 'bestselling', label: 'Bestselling' },
  { value: 'newest', label: 'Newest' },
  { value: 'price-asc', label: 'Price low to high' },
  { value: 'price-desc', label: 'Price high to low' },
  { value: 'title', label: 'Title A-Z' },
];

function timestampValue(value: unknown) {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof (value as { toMillis?: () => number }).toMillis === 'function') return (value as { toMillis: () => number }).toMillis();
  if (typeof (value as { seconds?: number }).seconds === 'number') return (value as { seconds: number }).seconds * 1000;
  return 0;
}

export function stableShopCartId(product: Pick<ShopProduct, 'id' | 'slug' | 'title'>) {
  const source = product.id || product.slug || product.title;
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
  return Math.abs(hash);
}

function normalizeTitleProduct(title: TitleDoc): ShopProduct {
  const category = title.category?.trim() || title.tags?.[0] || 'Books';
  const storefrontCoverUrl = title.storefrontCoverUrl || title.coverUrl;
  const storefrontCoverAlt = title.storefrontCoverAlt || title.coverAlt || title.title;
  const product = {
    id: title.titleId || title.slug,
    cartId: 0,
    itemType: 'title' as const,
    titleId: title.titleId,
    slug: title.slug,
    href: `${ROUTES.titles}/${title.slug}`,
    title: title.title,
    subtitle: title.subtitle || '',
    description: [title.shortDescription, title.marketingHook, title.subtitle, title.description].find(value => typeof value === 'string' && value.trim()) ?? '',
    category,
    tags: title.tags ?? [],
    formats: title.formats?.length ? title.formats : ['ebook'],
    price: Number(title.price || 0),
    compareAtPrice: Number((title as TitleDoc & { compareAtPrice?: number }).compareAtPrice || 0) || undefined,
    currency: title.currency || 'EUR',
    coverUrl: storefrontCoverUrl,
    coverAlt: storefrontCoverAlt,
    digitalSaleState: digitalSaleState(title),
    availableDigitalFormats: saleFormats(title),
    canPurchase: saleFormats(title).length > 0,
    featured: Boolean(title.featured),
    salesCount: Number(title.salesCount || title.salesCountActual || title.salesCountBaseline || 0),
    updatedAt: title.updatedAt,
    publishedAt: title.publishedAt,
  } satisfies Omit<ShopProduct, 'cartId'> & { cartId: number };

  return { ...product, cartId: stableShopCartId(product) };
}

function normalizeCollectionProduct(collection: PlatformCollectionDoc): ShopProduct | null {
  if (collection.collectionType !== 'bundle') return null;
  if (collection.deletionPending || collection.visibility !== 'published' || collection.publicationStatus !== 'published') return null;
  const includedTitleIds = collection.includedTitleIds?.filter(Boolean) ?? [];
  if (includedTitleIds.length < 2) return null;
  const currentMembers = new Map((collection.includedTitleSnapshots || []).map(title => [title.titleId, title]));
  const includedProducts = includedTitleIds.flatMap(id => {
    const title = currentMembers.get(id);
    return title ? [normalizeTitleProduct(title as TitleDoc)] : [];
  });
  const snapshotTotal = (collection.includedTitleSnapshots ?? []).reduce((sum, item) => sum + Number(item.price || 0), 0);
  const productTotal = includedProducts.reduce((sum, item) => sum + item.price, 0);
  const price = Number(collection.price || 0);
  if (!price || price <= 0) return null;
  const compareAtPrice = typeof collection.compareAtPrice === 'number' ? collection.compareAtPrice : snapshotTotal || productTotal || undefined;
  const availableDigitalFormats = collection.resolvedDigitalFormats || [];
  const product = {
    id: collection.collectionId,
    cartId: 0,
    itemType: 'bundle' as const,
    collectionId: collection.collectionId,
    slug: collection.slug || collection.collectionId,
    href: `${ROUTES.collections}/${collection.slug || collection.collectionId}`,
    title: collection.title,
    subtitle: collection.subtitle || '',
    description: collection.shortDescription || collection.description || `${includedProducts.length} books in one bundle.`,
    category: collection.category || 'Bundles',
    tags: collection.tags ?? [],
    formats: collection.formats?.length ? collection.formats : ['ebook'],
    availableDigitalFormats,
    price,
    compareAtPrice: compareAtPrice && compareAtPrice > price ? compareAtPrice : undefined,
    currency: collection.currency || includedProducts[0]?.currency || 'EUR',
    coverUrl: collection.coverUrl || collection.heroImageUrl || includedProducts[0]?.coverUrl,
    coverAlt: collection.coverAlt || collection.heroImageAlt || collection.title,
    canPurchase: Boolean(collection.isPurchasable && availableDigitalFormats.length),
    featured: Boolean(collection.isFeatured),
    includedTitleIds,
    includedProducts,
    discountLabel: collection.discountLabel,
    salesCount: Number(collection.salesCount || collection.salesCountActual || collection.salesCountBaseline || 0),
    updatedAt: collection.updatedAt,
    publishedAt: collection.updatedAt,
  } satisfies Omit<ShopProduct, 'cartId'> & { cartId: number };
  return { ...product, cartId: stableShopCartId(product) };
}

function productSearchText(product: ShopProduct) {
  return [
    product.title,
    product.subtitle,
    product.description,
    product.category,
    ...product.tags,
    ...product.formats,
  ].join(' ').toLowerCase();
}

export function filterShopProducts(products: ShopProduct[], categoryId: ShopCategoryId) {
  const category = SHOP_REFERENCE_CATEGORIES.find((item) => item.id === categoryId) ?? SHOP_REFERENCE_CATEGORIES[0];
  if (category.id === 'all') return products;
  if (category.id === 'bundles') return products.filter((product) => product.itemType === 'bundle');
  return products.filter((product) => {
    const haystack = productSearchText(product);
    return category.terms.some((term) => haystack.includes(term)) || product.category.toLowerCase() === category.label.toLowerCase();
  });
}

export function sortShopProducts(products: ShopProduct[], sort: ShopSortKey) {
  return [...products].sort((a, b) => {
    if (sort === 'price-asc') return a.price - b.price;
    if (sort === 'price-desc') return b.price - a.price;
    if (sort === 'title') return a.title.localeCompare(b.title);
    if (sort === 'newest') return timestampValue(b.publishedAt ?? b.updatedAt) - timestampValue(a.publishedAt ?? a.updatedAt);
    return b.salesCount - a.salesCount || Number(b.featured) - Number(a.featured) || a.title.localeCompare(b.title);
  });
}

function pickProductsByIds(products: ShopProduct[], ids?: string[]) {
  const wanted = new Set((ids ?? []).filter(Boolean));
  if (!wanted.size) return [];
  return products.filter((product) => wanted.has(product.titleId || '') || wanted.has(product.collectionId || '') || wanted.has(product.slug));
}

export function formatShopMoney(value: number, currency: 'EUR' | 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value || 0));
}

export function createShopViewModel(
  titles: TitleDoc[],
  settings?: HomepageSettingsDoc | null,
  collections: PlatformCollectionDoc[] = [],
  options: { checkoutAvailable?: boolean } = {},
): ShopViewModel {
  const checkoutAvailable = Boolean(options.checkoutAvailable);
  const titleProducts = titles.map(normalizeTitleProduct).map((product) => ({
    ...product,
    canPurchase: product.canPurchase && checkoutAvailable,
  }));
  const bundleProducts = collections.flatMap((collection) => {
    const product = normalizeCollectionProduct(collection);
    return product ? [product] : [];
  }).map((product) => ({
    ...product,
    canPurchase: product.canPurchase && checkoutAvailable,
  }));
  const products = [...bundleProducts, ...titleProducts];
  const featuredBySettings = pickProductsByIds(titleProducts, settings?.shopFeaturedCollectionTitleIds);
  const featuredProducts = featuredBySettings.length ? featuredBySettings : titleProducts.filter((product) => product.featured).slice(0, 3);
  const resolvedFeaturedProducts = settings?.shopFeaturedCollectionTitleIds !== undefined ? featuredBySettings : featuredProducts.length ? featuredProducts : titleProducts.slice(0, 3);
  const configuredBundle = bundleProducts.find((product) => settings?.shopBundleTitleIds?.includes(product.collectionId || product.slug));
  const featuredBundle = settings?.shopBundleTitleIds !== undefined ? configuredBundle ?? null : bundleProducts.find((product) => product.featured) || bundleProducts[0] || null;

  return {
    hero: {
      eyebrow: settings?.shopHeroEyebrow ?? 'The Untangled Shop',
      title: settings?.shopHeroTitle ?? 'Books for clarity, healing, and coming home to yourself.',
      description: settings?.shopHeroDescription ?? 'Thoughtful books and journals to help you set boundaries, trust yourself, heal deeply, and live with more peace.',
      ctaLabel: settings?.shopHeroCtaLabel ?? 'Browse all books',
      ctaHref: settings?.shopHeroCtaHref ?? '#shop-books',
      imageUrl: settings?.storefrontBannerImageUrl ?? settings?.shopFeaturedCollectionImageUrl ?? settings?.heroImageUrl,
      imageAlt: settings?.storefrontBannerImageAlt ?? settings?.shopFeaturedCollectionImageAlt ?? settings?.heroImageAlt ?? 'The Untangled Life shop',
    },
    categories: SHOP_REFERENCE_CATEGORIES,
    products,
    featuredCollection: {
      eyebrow: 'Featured collection',
      title: settings?.shopFeaturedCollectionTitle ?? 'Start Here',
      description: settings?.shopFeaturedCollectionDescription ?? 'Begin your journey with these reader-loved books that lay the foundation for real change.',
      ctaLabel: settings?.shopFeaturedCollectionCtaLabel ?? 'Explore the collection',
      imageUrl: settings?.shopFeaturedCollectionImageUrl ?? resolvedFeaturedProducts[0]?.coverUrl ?? settings?.defaultTitleCoverUrl,
      imageAlt: settings?.shopFeaturedCollectionImageAlt ?? resolvedFeaturedProducts[0]?.coverAlt ?? settings?.defaultTitleCoverAlt ?? 'Featured Untangled Life collection',
      products: resolvedFeaturedProducts,
    },
    bundle: featuredBundle
      ? {
          eyebrow: 'Reader favorite bundle',
          title: settings?.shopBundleTitle ?? featuredBundle.title,
          description: settings?.shopBundleDescription ?? featuredBundle.description,
          price: featuredBundle.price,
          compareAtPrice: featuredBundle.compareAtPrice,
          currency: featuredBundle.currency,
          imageUrl: settings?.shopBundleImageUrl ?? featuredBundle.coverUrl,
          imageAlt: settings?.shopBundleImageAlt ?? featuredBundle.coverAlt ?? 'Reader favorite bundle',
          products: featuredBundle.includedProducts ?? [],
          product: featuredBundle,
        }
      : null,
    benefits: (settings?.shopBenefitItems !== undefined
      ? settings.shopBenefitItems.map((item) => ({
          title: typeof item.title === 'string' ? item.title : '',
          description: typeof item.description === 'string' ? item.description : '',
          icon: item.icon || 'leaf',
        }))
      : [
          { title: 'Digital-first editions', description: 'Published files are delivered through your reader account.', icon: 'download' },
          { title: 'Private reader library', description: 'Keep eligible book downloads together in one account.', icon: 'book' },
          { title: 'Availability shown clearly', description: 'Purchase controls appear only when checkout is open.', icon: 'lock' },
          { title: 'Reader resources', description: 'Guides, reflections, and tools in every book.', icon: 'leaf' },
        ]).map((item) => {
          const title = item.title.trim().toLowerCase();
          if (title.includes('paperback')) {
            return { title: 'Digital-first editions', description: 'Print availability is shown on an individual title only when a verified retailer link exists.', icon: 'book' };
          }
          if (!checkoutAvailable && title.includes('secure checkout')) {
            return { title: 'Checkout in qualification', description: 'Purchase controls stay closed until a payment provider is fully configured.', icon: 'lock' };
          }
          if (!checkoutAvailable && title.includes('instant')) {
            return { title: 'Digital delivery after purchase', description: 'Eligible files appear after checkout is activated and payment is verified.', icon: 'download' };
          }
          return item;
        }),
    newsletter: {
      title: settings?.shopNewsletterTitle ?? 'Letters for your healing journey',
      description: settings?.shopNewsletterDescription ?? 'Weekly reflections, new releases, and gentle reminders to keep choosing yourself.',
      imageUrl: settings?.shopNewsletterImageUrl ?? settings?.leadMagnetImageUrl,
      imageAlt: settings?.shopNewsletterImageAlt ?? settings?.leadMagnetImageAlt ?? 'The Untangled Life newsletter',
    },
  };
}
